import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Delay (ms) before opening a submenu on pointer hover.
 * Prevents the menu from flashing open when the cursor merely passes over a menu item.
 * Keyboard focus activation is NOT subject to this delay (accessibility).
 */
const HOVER_OPEN_DELAY = 100;

/**
 * Delay (ms) before closing a submenu after the pointer leaves.
 * Gives the user time to move the cursor into the submenu itself, and gives the
 * CSS close transition time to actually play before `visibility: hidden` is applied.
 * Keyboard blur deactivation is NOT subject to this delay (accessibility).
 */
const HOVER_CLOSE_DELAY = 300;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Pending hover-intent timeout for opening a submenu.
   * @type {number | null}
   */
  #openTimeout = null;

  /**
   * Pending hover-intent timeout for closing a submenu.
   * @type {number | null}
   */
  #closeTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    // Listen directly on each submenu panel too. The panel is positioned absolutely
    // and can render visually apart from its trigger `<li>`, so relying solely on the
    // `<li>`'s own pointerenter/pointerleave containment isn't reliable if the cursor's
    // path briefly crosses an unrelated element on the way down. Entering the submenu
    // itself should always cancel a pending close; leaving it should schedule one.
    this.refs.submenu?.forEach((submenu) => {
      submenu.addEventListener('pointerenter', this.#submenuPointerEnter);
      submenu.addEventListener('pointerleave', this.#submenuPointerLeave);
    });

    // Safety net: if the user has started a click (pointerdown) inside the currently
    // open submenu, never let a scheduled close beat them to it.
    this.addEventListener('pointerdown', this.#handlePointerDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.refs.submenu?.forEach((submenu) => {
      submenu.removeEventListener('pointerenter', this.#submenuPointerEnter);
      submenu.removeEventListener('pointerleave', this.#submenuPointerLeave);
    });
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#cleanupMutationObserver();
    this.#clearOpenTimeout();
    this.#clearCloseTimeout();
  }

  /**
   * Entering a submenu panel directly should always cancel any pending open/close,
   * regardless of how the pointer got there.
   */
  #submenuPointerEnter = () => {
    this.#clearOpenTimeout();
    this.#clearCloseTimeout();
  };

  /**
   * Leaving a submenu panel schedules the same debounced close as leaving the trigger.
   */
  #submenuPointerLeave = () => {
    this.#scheduleClose();
  };

  /**
   * If the user starts clicking (pointerdown) inside the currently active submenu,
   * cancel any pending close so the click isn't lost to the menu disappearing first.
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (!(event.target instanceof Node)) return;
    const activeMenu = findSubmenu(this.#state.activeItem);
    if (activeMenu?.contains(event.target)) {
      this.#clearCloseTimeout();
    }
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#clearOpenTimeout();
    this.#scheduleClose();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Entry point for activation. Pointer-driven activation (hover) is delayed by
   * HOVER_OPEN_DELAY to implement hover-intent; keyboard-driven activation (focus)
   * happens immediately so keyboard users aren't slowed down.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) {
      // Re-entering the already-active item (or its submenu) should cancel any pending close.
      this.#clearCloseTimeout();
      return;
    }

    // Cancel any pending close now that we're (re-)activating.
    this.#clearCloseTimeout();

    if (event.type === 'pointerenter') {
      this.#clearOpenTimeout();
      this.#openTimeout = window.setTimeout(() => {
        this.#openTimeout = null;
        this.#activateItem(item, event);
      }, HOVER_OPEN_DELAY);
    } else {
      // Focus (keyboard) activation: no delay.
      this.#clearOpenTimeout();
      this.#activateItem(item, event);
    }
  };

  /**
   * Actually perform the activation of a menu item (opens its submenu).
   * Split out from `activate` so it can be invoked immediately (focus) or after
   * the hover-intent delay (pointer).
   * @param {HTMLElement} item
   * @param {PointerEvent | FocusEvent} event
   */
  #activateItem = (item, event) => {
    if (item == this.#state.activeItem) return;

    const isDefaultSlot = event.target instanceof Element && event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Entry point for deactivation. Pointer-driven deactivation (pointerleave) is
   * delayed by HOVER_CLOSE_DELAY so the user has time to move into the submenu and
   * so the CSS close transition can actually play. Keyboard-driven deactivation
   * (blur) happens immediately.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // A leave event means we're no longer heading toward opening this item.
    this.#clearOpenTimeout();

    if (event.type === 'pointerleave') {
      this.#scheduleClose();
    } else {
      // Blur (keyboard) deactivation: no delay.
      this.#deactivate();
    }
  }

  /**
   * Schedule a deactivation after HOVER_CLOSE_DELAY, cancelling any previously
   * scheduled close first. Shared by the trigger, the submenu panel, and the
   * overflow ("More") menu so all three behave identically.
   */
  #scheduleClose() {
    this.#clearCloseTimeout();
    this.#closeTimeout = window.setTimeout(() => {
      this.#closeTimeout = null;
      this.#deactivate();
    }, HOVER_CLOSE_DELAY);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #clearOpenTimeout() {
    if (this.#openTimeout !== null) {
      window.clearTimeout(this.#openTimeout);
      this.#openTimeout = null;
    }
  }

  #clearCloseTimeout() {
    if (this.#closeTimeout !== null) {
      window.clearTimeout(this.#closeTimeout);
      this.#closeTimeout = null;
    }
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}